import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Alert,
  Stack,
} from '@mui/material';
import { Download, Refresh } from '@mui/icons-material';
import { saveAs } from 'file-saver';
import VerifyButton from './VerifyButton'; // Or replace with MUI Button if needed
import BatchManagement from './BatchManagement';

const BulkEmailVerification = ({ emails, onVerificationComplete, handleDownloadCSV, csvLoading }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [skippedEmails, setSkippedEmails] = useState([]);

  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleVerify = async () => {
    if (!emails || emails.length === 0) {
      alert('Please provide at least one email.');
      return;
    }

    const validEmails = emails.filter(e => isValidEmail(e.email));
    const skipped = emails.filter(e => !isValidEmail(e.email));
    setSkippedEmails(skipped);

    if (validEmails.length === 0) {
      alert('No valid emails to verify.');
      return;
    }

    setLoading(true);
    setProgress(0);

    try {
      // Backend handles Debounce call + DB update — no API key needed in frontend
      const response = await axios.post('/api/v1/bulk-verify-emails', {
        emails: validEmails, // [{id, email}, ...]
      });

      const data = response.data;
      if (data.success) {
        setResults(data.results);
        setProgress(100);
        onVerificationComplete();
      } else {
        alert('Verification failed: ' + (data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Bulk verify error:', err);
      alert('Error calling verification API. See console.');
    }

    setLoading(false);
  };

  const handleClearResults = () => {
    setResults([]);
    setProgress(0);
    setSkippedEmails([]);
  };

  const handleDownloadVerificationCSV = () => {
    if (results.length === 0) return;

    const headers = ['Email', 'Status', 'Reason'];
    const rows = results.map(res => [res.email, res.result, res.reason]);

    const csvContent = [headers, ...rows].map(e => e.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'email_verification_results.csv');
  };

  return (
    <Box maxWidth="md" my={-4.5} mb={4}>
      <Stack direction="row" spacing={2} mb={2} ml={23}>
        <VerifyButton onClick={handleVerify} loading={loading} />
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={handleDownloadCSV}
          disabled={csvLoading}
        >
          {csvLoading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Preparing Excel...
            </>
          ) : (
            "Download Excel"
          )}
        </Button>

      </Stack>

      {loading && (
        <Box my={3}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography align="center" mt={1}>{progress}%</Typography>
        </Box>
      )}

      {skippedEmails.length > 0 && (
        <Box mb={2}>
          <Alert severity="warning">
            <strong>Skipped invalid emails:</strong>
            <ul style={{ marginTop: '5px' }}>
              {skippedEmails.map((e, i) => <li key={i}>{e.email}</li>)}
            </ul>
          </Alert>
        </Box>
      )}

      {results.length > 0 && (
        <Paper elevation={3} sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Verification Results</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Download />}
                onClick={handleDownloadVerificationCSV}
              >
                Download CSV
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Refresh />}
                onClick={handleClearResults}
              >
                Clear
              </Button>
            </Stack>
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Reason</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((res, index) => (
                <TableRow key={index}>
                  <TableCell>{res.email}</TableCell>
                  <TableCell>{res.result}</TableCell>
                  <TableCell>{res.reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default BulkEmailVerification;
